<template>
    <div class="row">
      <div class="col-md-12">
        <div class="card">
          <div class="card-header">
            <h4 class="card-title">{{ isEditing ? 'Editar Rol' : 'Crear Rol' }}</h4>
            <p class="card-category">
              {{ isEditing ? 'Modifique los datos del rol' : 'Complete el formulario para crear un nuevo rol' }}
            </p>
          </div>
          <div class="card-body">
            <div v-if="loading" class="text-center">
              <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Cargando...</span>
              </div>
            </div>
            
            <form v-else @submit.prevent="saveRole">
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Nombre del Rol</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      v-model="form.name" 
                      :class="{ 'is-invalid': errors.name }"
                      :disabled="isEditing && isProtectedRole"
                    >
                    <div v-if="errors.name" class="invalid-feedback">
                      {{ errors.name[0] }}
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="row">
                <div class="col-md-12">
                  <div class="form-group">
                    <label>Permisos</label>
                    <div class="permissions-container">
                      <div v-for="permission in permissions" :key="permission.id" class="form-check">
                        <input 
                          type="checkbox" 
                          :id="`permission-${permission.id}`" 
                          :value="permission.name" 
                          v-model="form.permissions" 
                          class="form-check-input"
                          :disabled="isEditing && form.name === 'admin'"
                        >
                        <label :for="`permission-${permission.id}`" class="form-check-label">
                          {{ permission.name }}
                        </label>
                      </div>
                    </div>
                    <div v-if="errors.permissions" class="text-danger">
                      {{ errors.permissions[0] }}
                    </div>
                  </div>
                </div>
              </div>
              
              <div v-if="isEditing && isProtectedRole" class="alert alert-warning">
                <i class="fa fa-exclamation-triangle"></i>
                Este es un rol predefinido del sistema. Algunas propiedades no pueden ser modificadas.
              </div>
              
              <div class="row">
                <div class="col-md-12 text-right">
                  <router-link to="/roles" class="btn btn-default">
                    Cancelar
                  </router-link>
                  <button type="submit" class="btn btn-primary" :disabled="saving">
                    {{ saving ? 'Guardando...' : 'Guardar' }}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { ref, computed, onMounted } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import axios from 'axios';
  import { useAuthStore } from '../../stores/authStore';
  import Swal from 'sweetalert2';
  
  export default {
    setup() {
      const route = useRoute();
      const router = useRouter();
      //const swal = useSwal();
      const authStore = useAuthStore();
      
      const form = ref({
        name: '',
        permissions: []
      });
      
      const permissions = ref([]);
      const loading = ref(true);
      const saving = ref(false);
      const errors = ref({});
      
      const roleId = computed(() => {
        return route.params.id;
      });
      
      const isEditing = computed(() => {
        return !!roleId.value;
      });
      
      const isProtectedRole = computed(() => {
        return ['admin', 'consultas', 'ventanilla', 'area', 'unidad', 'direccion'].includes(form.value.name);
      });
      
      const fetchPermissions = async () => {
        try {
          const response = await axios.get('/permissions');
          permissions.value = response.data.permissions;
        } catch (error) {
          console.error('Error al cargar permisos:', error);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudieron cargar los permisos'
          });
        }
      };
      
      const fetchRole = async () => {
        if (!isEditing.value) {
          loading.value = false;
          return;
        }
        
        try {
          const response = await axios.get(`/roles/${roleId.value}/show`);
          const role = response.data.role;
          
          form.value = {
            name: role.name,
            permissions: role.permissions.map(permission => permission.name)
          };
          
          // Si es el rol admin y el usuario no es admin, redirigir
          if (role.name === 'admin' && !authStore.hasRole('admin')) {
            Swal.fire({
              icon: 'error',
              title: 'Acceso denegado',
              text: 'No tiene permisos para editar este rol'
            });
            router.push('/roles');
          }
        } catch (error) {
          console.error('Error al cargar rol:', error);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudo cargar el rol'
          });
          router.push('/roles');
        } finally {
          loading.value = false;
        }
      };
      
      const saveRole = async () => {
        saving.value = true;
        errors.value = {};
        try {
          let response;
          if (isEditing.value) {
            response = await axios.post(`/roles/${roleId.value}/edit`, form.value);
          } else {
            response = await axios.post('/roles/create', form.value);
          }
          
          Swal.fire({
            icon: 'success',
            title: 'Éxito',
            text: response.data.message
          });
          
          router.push('/roles');
        } catch (error) {
          console.error('Error al guardar rol:', error);
          
          if (error.response?.status === 422) {
            errors.value = error.response.data.errors;
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: error.response?.data?.message || 'No se pudo guardar el rol'
            });
          }
        } finally {
          saving.value = false;
        }
      };
      
      onMounted(async () => {
        await Promise.all([fetchPermissions(), fetchRole()]);
      });
      
      return {
        form,
        permissions,
        loading,
        saving,
        errors,
        isEditing,
        isProtectedRole,
        saveRole
      };
    }
  };
  </script>
  
  <style scoped>
  .permissions-container {
    border: 1px solid #e9ecef;
    padding: 10px;
    border-radius: 4px;
    max-height: 200px;
    overflow-y: auto;
  }
  </style>